import './style.scss'
import React from 'react'
import styled, { css } from 'styled-components'
import {
    tokens, rem, UIProps, Variant, Size, IconPosition,
} from 'src/componentv2/Base/ui'

export interface ButtonProps
    extends UIProps, Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>
{
    variant?: Variant
    size?: Size
    block?: boolean
    icon?: React.ReactNode
    iconPosition?: IconPosition
    disabled?: boolean
    fontFamily?: string
}

const variantStyles: Record<Variant, ReturnType<typeof css>> = {
    primary: css`
        background: ${tokens.primary};
        color: ${tokens.white};
        border: 1px solid ${tokens.primary};
        &:hover:not(:disabled) { background: ${tokens.primaryHover}; border-color: ${tokens.primaryHover}; }
    `,
    success: css`
        background: ${tokens.success};
        color: ${tokens.white};
        border: 1px solid ${tokens.success};
        &:hover:not(:disabled) { background: ${tokens.successHover}; border-color: ${tokens.successHover}; }
    `,
    outline: css`
        background: ${tokens.white};
        color: ${tokens.primary};
        border: 1px solid ${tokens.border};
        &:hover:not(:disabled) { background: ${tokens.primarySoft}; }
    `,
    ghost: css`
        background: ${tokens.primarySoft};
        color: ${tokens.primary};
        border: 1px solid transparent;
        &:hover:not(:disabled) { background: #D7EFEE; }
    `,
}

const sizeStyles: Record<Size, ReturnType<typeof css>> = {
    sm: css`height: 3.2rem; padding: 0 1.2rem; font-size: 1.3rem;`,
    md: css`height: 4rem;   padding: 0 1.8rem; font-size: 1.4rem;`,
    lg: css`height: 4.8rem; padding: 0 2.4rem; font-size: 1.6rem;`,
}

const StyledButton = styled.button<ButtonProps>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 0.8rem;
    border-radius: ${({ radius }) => rem(radius) ?? '0.8rem'};
    font-weight: ${({ fontWeight }) => fontWeight ?? '600'};
    font-family: ${({ fontFamily }) => fontFamily ?? "'Gilmer Light', sans-serif"};
    cursor: pointer;
    width: ${({ block, width }) => (block ? '100%' : rem(width) ?? 'auto')};
    transition: all 0.18s ease;
    white-space: nowrap;

    ${({ size = 'md' }) => sizeStyles[size]}
    ${({ variant = 'primary' }) => variantStyles[variant]}

    ${({ bgColor }) =>
            bgColor && css`background: ${bgColor}; border-color: ${bgColor};`}
    ${({ color }) => color && css`color: ${color};`}
    ${({ hoverBgColor }) =>
            hoverBgColor &&
            css`&:hover:not(:disabled) { background: ${hoverBgColor}; border-color: ${hoverBgColor}; }`}

    &:active:not(:disabled) { transform: translateY(1px); }
    &:disabled { opacity: 0.5; cursor: not-allowed; }
`
export const Button: React.FC<ButtonProps> = ({
                                                  children, icon, iconPosition = 'left', className = '', ...rest
                                              }) => (
    <StyledButton className={`button ${className}`} {...rest}>
        {icon && iconPosition === 'left' && (
            <span className="button__icon">{icon}</span>
        )}
        {children}
        {icon && iconPosition === 'right' && (
            <span className="button__icon">{icon}</span>
        )}
    </StyledButton>
)

export default Button